import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { dateTime, money } from "../jobs/types";
import type { Offer, Schedule } from "./types";

export function Interval({
  schedule,
  locale,
}: {
  schedule: Schedule;
  locale: string;
}) {
  return (
    <div className="space-y-1 break-words">
      <p>{schedule.bay_name}</p>
      <p>
        {dateTime(schedule.starts_at, schedule.time_zone, locale)} –{" "}
        {dateTime(schedule.ends_at, schedule.time_zone, locale)}
      </p>
      <p className="text-muted-foreground text-sm">{schedule.time_zone}</p>
    </div>
  );
}

export default function RescheduleDetails({ offer }: { offer: Offer }) {
  const { i18n } = useTranslation();
  const t = i18n.getFixedT(offer.representation.locale);
  const represented = offer.representation;
  return (
    <article className="space-y-4" data-testid="reschedule-details">
      <h2 className="text-lg font-semibold break-words">
        {represented.customer.name} · {represented.vehicle.make}{" "}
        {represented.vehicle.model}
      </h2>
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>{t("reschedules.original")}</CardTitle>
          </CardHeader>
          <CardContent>
            <Interval
              schedule={offer.original_schedule}
              locale={represented.locale}
            />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>{t("reschedules.replacement")}</CardTitle>
          </CardHeader>
          <CardContent>
            <Interval
              schedule={represented.schedule}
              locale={represented.locale}
            />
          </CardContent>
        </Card>
      </div>
      <dl className="space-y-3 break-words">
        <div>
          <dt className="font-medium">{t("reschedules.reason")}</dt>
          <dd className="whitespace-pre-wrap">{offer.reason}</dd>
        </div>
        <div>
          <dt className="font-medium">{t("reschedules.communication")}</dt>
          <dd className="whitespace-pre-wrap">{offer.communication}</dd>
        </div>
      </dl>
      {offer.state === "pending" && (
        <p className="text-muted-foreground text-sm">
          {t("reschedules.keep_original")}
        </p>
      )}
      {(offer.state === "pending" || offer.state === "expired") && (
        <p>
          {t(
            offer.state === "pending"
              ? "reschedules.deadline"
              : "reschedules.expired_at",
            {
              expiry: dateTime(
                offer.hold_expires_at,
                represented.schedule.time_zone,
                represented.locale,
              ),
            },
          )}
        </p>
      )}
      <p role="status" className="rounded-md border p-3">
        {t(`reschedules.${offer.state}_status`)}
      </p>
      {offer.suggested_time && (
        <p className="break-words whitespace-pre-wrap">
          {t("reschedules.suggested", { time: offer.suggested_time })}
        </p>
      )}
      {offer.note && (
        <p className="break-words whitespace-pre-wrap">{offer.note}</p>
      )}
      <details className="rounded-lg border p-4">
        <summary className="cursor-pointer font-medium">
          {t("reschedules.unchanged_terms")}
        </summary>
        <div className="mt-4 space-y-3">
          <p>{represented.staff.map((person) => person.name).join(", ")}</p>
          {represented.total.lines.map((line) => (
            <div
              key={line.line_id}
              className="flex flex-wrap justify-between gap-2 border-b pb-2"
            >
              <p className="break-words">
                {line.name} × {line.quantity}
              </p>
              <p>
                {money(
                  line.total_minor,
                  represented.total.currency,
                  represented.locale,
                )}
              </p>
            </div>
          ))}
          <p className="font-semibold">
            {t("jobs.total_minor")}:{" "}
            {money(
              represented.total.total_minor,
              represented.total.currency,
              represented.locale,
            )}
          </p>
        </div>
      </details>
    </article>
  );
}
